'use client';

import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import {
  selectOptimizedCriteria,
  selectTerritoryActionPlans,
  selectSelectedTerritories,
  selectExpandedReps,
  selectCurrentProcessingTerritory,
  selectDynamicCriteria,
  selectTextParts,
  setOptimizedCriteria,
  setActionPlan,
  updatePlanStatus,
  updateListName,
  removePlan,
  setCurrentProcessingTerritory,
  clearActionPlans,
  toggleTerritory,
  clearTerritories,
  toggleExpandedRep,
  clearExpandedReps,
  setDynamicCriteria,
  updateCriteriaTemplate,
  updateComponentInput,
  removeComponentInput,
  clearDynamicCriteria,
  updateTextPart,
  setTextParts,
  addTextPart,
  removeTextPart,
  resetAll,
  TerritoryActionPlan,
  DynamicCriteria,
  DynamicComponentInput,
  TextPart,
} from 'src/redux/slices/aiListGeneratorSlice';
import { LOCATION } from '../filters/constants';

export const useAiGenerator = (location = LOCATION.AI_LIST) => {
  const dispatch = useDispatch();

  // Selectors
  const optimizedCriteria = useSelector(selectOptimizedCriteria);
  const territoryActionPlans = useSelector(selectTerritoryActionPlans);
  const selectedTerritories = useSelector(selectSelectedTerritories);
  const expandedReps = useSelector(selectExpandedReps);
  const currentProcessingTerritory = useSelector(
    selectCurrentProcessingTerritory
  );
  const dynamicCriteria = useSelector(selectDynamicCriteria(location));
  const textParts = useSelector(selectTextParts(location));

  // Action creators
  const setOptimizedCriteriaAction = useCallback(
    (criteria: string) => {
      dispatch(setOptimizedCriteria(criteria));
    },
    [dispatch]
  );

  const setActionPlanForTerritory = useCallback(
    (plan: TerritoryActionPlan) => {
      dispatch(setActionPlan(plan));
    },
    [dispatch]
  );

  const updatePlanStatusAction = useCallback(
    (
      territoryId: string | null,
      status: TerritoryActionPlan['status'],
      dashboardId?: string
    ) => {
      dispatch(updatePlanStatus({ territoryId, status, dashboardId }));
    },
    [dispatch]
  );

  const updateListNameAction = useCallback(
    (territoryId: string | null, listName: string) => {
      dispatch(updateListName({ territoryId, listName }));
    },
    [dispatch]
  );

  const removePlanAction = useCallback(
    (territoryId: string | null) => {
      dispatch(removePlan(territoryId));
    },
    [dispatch]
  );

  const setCurrentProcessingTerritoryAction = useCallback(
    (territoryId: string | null) => {
      dispatch(setCurrentProcessingTerritory(territoryId));
    },
    [dispatch]
  );

  const clearActionPlansAction = useCallback(() => {
    dispatch(clearActionPlans());
  }, [dispatch]);

  const setSelectedTerritory = useCallback(
    (territoryId: string) => {
      dispatch(toggleTerritory(territoryId));
    },
    [dispatch]
  );

  const clearTerritoriesAction = useCallback(() => {
    dispatch(clearTerritories());
  }, [dispatch]);

  const setExpandedReps = useCallback(
    (repName: string) => {
      dispatch(toggleExpandedRep(repName));
    },
    [dispatch]
  );

  const clearExpandedRepsAction = useCallback(() => {
    dispatch(clearExpandedReps());
  }, [dispatch]);

  const resetAllAction = useCallback(() => {
    dispatch(resetAll());
  }, [dispatch]);

  // Dynamic criteria actions
  const setDynamicCriteriaAction = useCallback(
    (criteria: DynamicCriteria) => {
      dispatch(setDynamicCriteria({ criteria, location }));
    },
    [dispatch, location]
  );

  const updateCriteriaTemplateAction = useCallback(
    (template: string) => {
      dispatch(updateCriteriaTemplate({ template, location }));
    },
    [dispatch, location]
  );

  const updateComponentInputAction = useCallback(
    (componentId: string, input: DynamicComponentInput) => {
      dispatch(updateComponentInput({ componentId, input, location }));
    },
    [dispatch, location]
  );

  const removeComponentInputAction = useCallback(
    (componentId: string) => {
      dispatch(removeComponentInput({ componentId, location }));
    },
    [dispatch, location]
  );

  const clearDynamicCriteriaAction = useCallback(() => {
    dispatch(clearDynamicCriteria({ location }));
  }, [dispatch, location]);

  // TextParts actions
  const updateTextPartAction = useCallback(
    (index: number, content: string) => {
      dispatch(updateTextPart({ index, content, location }));
    },
    [dispatch, location]
  );

  const setTextPartsAction = useCallback(
    (parts: TextPart[]) => {
      dispatch(setTextParts({ parts, location }));
    },
    [dispatch, location]
  );

  const addTextPartAction = useCallback(
    (part: TextPart, index: number) => {
      dispatch(addTextPart({ part, index, location }));
    },
    [dispatch, location]
  );

  const removeTextPartAction = useCallback(
    (index: number) => {
      dispatch(removeTextPart({ index, location }));
    },
    [dispatch, location]
  );

  return {
    // Derived getters
    optimizedCriteria,
    territoryActionPlans,
    selectedTerritories,
    expandedReps,
    currentProcessingTerritory,
    dynamicCriteria,
    textParts,
    // Action creators - using the exact same names as the original context
    setOptimizedCriteria: setOptimizedCriteriaAction,
    setActionPlanForTerritory,
    updatePlanStatus: updatePlanStatusAction,
    updateListName: updateListNameAction,
    removePlan: removePlanAction,
    setCurrentProcessingTerritory: setCurrentProcessingTerritoryAction,
    clearActionPlans: clearActionPlansAction,
    setSelectedTerritory,
    clearTerritories: clearTerritoriesAction,
    setExpandedReps,
    clearExpandedReps: clearExpandedRepsAction,
    resetAll: resetAllAction,
    // Dynamic criteria actions
    setDynamicCriteria: setDynamicCriteriaAction,
    updateCriteriaTemplate: updateCriteriaTemplateAction,
    updateComponentInput: updateComponentInputAction,
    removeComponentInput: removeComponentInputAction,
    clearDynamicCriteria: clearDynamicCriteriaAction,
    // TextParts actions
    updateTextPart: updateTextPartAction,
    setTextParts: setTextPartsAction,
    addTextPart: addTextPartAction,
    removeTextPart: removeTextPartAction,
  };
};
